// Root-level error boundary. Catches JavaScript errors anywhere in the
// child component tree, logs them, and shows a graceful fallback UI
// instead of leaving the user with a blank page.
//
// Design decisions:
//   * Class component because React error boundaries still require it.
//   * Never re-render a broken subtree — a stuck retry loop is worse
//     than a hard reload. The Reload button routes to `/` so the SPA
//     can rehydrate from scratch.
//   * No PHI in the fallback message. The error itself may contain
//     stack frames referencing clinical component names — we log to
//     the console in dev, but only display a generic "Something went
//     wrong" line to the user.
//   * `resetKey` prop (route pathname) forces a boundary reset when
//     the user navigates away — so hitting a broken page then going
//     Home doesn't trap them behind a stale error.

import React from "react";

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Dev console. Production ships to Sentry via `window.onerror` +
    // the reporter installed in `main.js`.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info?.componentStack);
  }

  componentDidUpdate(prevProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          data-testid="root-error-boundary"
          className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center px-6"
        >
          <div className="max-w-md text-center space-y-6">
            <div className="text-6xl">·</div>
            <h1 className="text-2xl font-serif">Something went wrong</h1>
            <p className="text-sm text-slate-400 leading-relaxed">
              We hit an unexpected problem. Your session is safe — nothing was lost.
              Please reload to continue.
            </p>
            <button
              type="button"
              onClick={() => { window.location.href = "/"; }}
              data-testid="error-boundary-reload-btn"
              className="rounded-full bg-amber-400 text-slate-950 px-6 py-2.5 text-sm font-medium hover:bg-amber-300 transition-colors"
            >
              Reload the app
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
